<template>
  <div class="budgets-lider-page">
    <nav class="page-breadcrumb mb-1">
      <feather-icon icon="HomeIcon" size="14" />
      <span class="ml-25">Operações</span>
      <feather-icon icon="ChevronRightIcon" size="14" class="mx-25" />
      <span class="font-weight-bold">Budget do Líder de Vendas</span>
    </nav>

    <b-alert show variant="primary" class="mb-1">
      <div class="p-50 small">
        <feather-icon icon="InfoIcon" class="mr-50" />
        Este é um orçamento <strong>paralelo e separado</strong> do budget normal do
        supervisor. A verba é atribuída a um supervisor e earmarcada por
        <strong>cliente</strong> ou <strong>representante</strong>. Doações para um
        cliente earmarcado consomem esta verba, e não a verba normal do representante.
      </div>
    </b-alert>

    <b-card class="mb-1">
      <b-row class="align-items-end">
        <b-col md="4" sm="12">
          <label class="small text-muted">Supervisor</label>
          <v-select
            v-model="filters.supervisorid"
            :options="supervisores"
            :reduce="s => s.supervisorid"
            label="supervisornome"
            placeholder="Todos"
            class="budget-vselect-sm"
            @input="buscar"
          >
            <template #no-options>Nenhum supervisor encontrado</template>
          </v-select>
        </b-col>
        <b-col md="3" sm="6">
          <label class="small text-muted">Situação</label>
          <b-form-select v-model="filters.vigentes" size="sm" @change="buscar">
            <b-form-select-option :value="null">Todos</b-form-select-option>
            <b-form-select-option value="1">Somente vigentes</b-form-select-option>
          </b-form-select>
        </b-col>
        <b-col md="5" sm="12" class="d-flex align-items-end" style="gap:6px">
          <b-button size="sm" variant="outline-secondary" @click="limparFiltros">
            <feather-icon icon="XIcon" size="13" class="mr-25" />Limpar
          </b-button>
          <b-button v-if="$can('manage', 'BudgetLider')" size="sm" variant="relief-success" class="ml-auto" @click="abrirNovo">
            <feather-icon icon="PlusIcon" size="13" class="mr-25" />Novo Budget do Líder
          </b-button>
        </b-col>
      </b-row>
    </b-card>

    <b-card>
      <div v-if="isLoading" class="text-center py-4"><b-spinner /></div>
      <div v-else-if="!budgets.length" class="text-center py-4 text-muted">
        Nenhum budget do líder encontrado.
      </div>
      <b-table
        v-else
        :items="budgets"
        :fields="fields"
        responsive
        hover
        small
        class="mb-0"
      >
        <template #cell(supervisor_nome)="data">
          <span class="font-weight-bold">{{ data.item.supervisor_nome || data.item.supervisorid }}</span>
        </template>
        <template #cell(lider_nome)="data">
          {{ data.item.lider_nome || '-' }}
        </template>
        <template #cell(periodo)="data">
          {{ formatarData(data.item.datainicio) }} — {{ formatarData(data.item.datatermino) }}
        </template>
        <template #cell(valor)="data">
          {{ formatarMoeda(data.item.valor) }}
        </template>
        <template #cell(valorgasto)="data">
          {{ formatarMoeda(data.item.valorgasto) }}
        </template>
        <template #cell(saldo)="data">
          <span :class="Number(data.item.saldo) <= 0 ? 'text-danger font-weight-bold' : 'text-success'">
            {{ formatarMoeda(data.item.saldo) }}
          </span>
        </template>
        <template #cell(ativo)="data">
          <b-badge :variant="data.item.ativo ? 'light-success' : 'light-secondary'">
            {{ data.item.ativo ? 'Ativo' : 'Inativo' }}
          </b-badge>
        </template>
        <template #cell(acoes)="data">
          <b-button size="sm" variant="flat-primary" class="btn-icon" @click="verDetalhes(data.item)">
            <feather-icon icon="EyeIcon" size="14" />
          </b-button>
          <b-button v-if="$can('manage', 'BudgetLider')" size="sm" variant="flat-secondary" class="btn-icon" @click="editar(data.item)">
            <feather-icon icon="EditIcon" size="14" />
          </b-button>
          <b-button v-if="$can('manage', 'BudgetLider')" size="sm" variant="flat-danger" class="btn-icon" @click="confirmarExclusao(data.item)">
            <feather-icon icon="Trash2Icon" size="14" />
          </b-button>
        </template>
      </b-table>
    </b-card>

    <budget-lider-modal ref="budgetModal" @salvo="buscar" />
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BFormSelect, BFormSelectOption, BButton, BSpinner, BTable, BBadge, BAlert,
} from 'bootstrap-vue';
import vSelect from 'vue-select';
import { mapActions, mapState } from 'vuex';
import Swal from 'sweetalert2';
import { useToast } from 'vue-toastification/composition';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import BudgetLiderModal from './modal/BudgetLiderModal.vue';

export default {
  name: 'BudgetsLiderPage',
  components: {
    BCard, BRow, BCol, BFormSelect, BFormSelectOption, BButton, BSpinner, BTable, BBadge, BAlert,
    vSelect,
    BudgetLiderModal,
  },
  setup() {
    const toast = useToast();
    return { toast };
  },
  data() {
    return {
      filters: { supervisorid: null, vigentes: null },
      fields: [
        { key: 'supervisor_nome', label: 'Supervisor' },
        { key: 'lider_nome', label: 'Líder' },
        { key: 'periodo', label: 'Período' },
        { key: 'valor', label: 'Valor', class: 'text-right' },
        { key: 'valorgasto', label: 'Gasto', class: 'text-right' },
        { key: 'saldo', label: 'Saldo', class: 'text-right' },
        { key: 'ativo', label: 'Situação', class: 'text-center' },
        { key: 'acoes', label: 'Ações', class: 'text-center' },
      ],
    };
  },
  computed: {
    ...mapState('BudgetLider', ['budgets', 'supervisores', 'isLoading']),
  },
  async mounted() {
    await this.carregarSupervisores();
    await this.buscar();
  },
  methods: {
    ...mapActions('BudgetLider', ['listar', 'carregarSupervisores', 'excluir', 'setFilters']),
    async buscar() {
      this.setFilters({ ...this.filters });
      await this.listar();
    },
    limparFiltros() {
      this.filters = { supervisorid: null, vigentes: null };
      this.buscar();
    },
    abrirNovo() {
      this.$refs.budgetModal.abrir();
    },
    editar(item) {
      this.$refs.budgetModal.abrir(item.budgetliderid);
    },
    verDetalhes(item) {
      this.$router.push({ name: 'budget-lider-detalhes', params: { id: item.budgetliderid } });
    },
    async confirmarExclusao(item) {
      const r = await Swal.fire({
        title: 'Excluir budget do líder?',
        text: `Supervisor: ${item.supervisor_nome || item.supervisorid}`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sim, excluir',
        cancelButtonText: 'Cancelar',
        customClass: { confirmButton: 'btn btn-danger', cancelButton: 'btn btn-outline-secondary ml-1' },
        buttonsStyling: false,
      });
      if (!r.isConfirmed) return;
      try {
        await this.excluir(item.budgetliderid);
        this.toast({
          component: ToastificationContent,
          props: { title: 'Budget do líder excluído', icon: 'CheckCircleIcon', variant: 'success' },
        });
      } catch (e) {
        // erro já exibido pelo client
      }
    },
    formatarMoeda(v) {
      const n = Number(v || 0);
      return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    },
    formatarData(d) {
      if (!d) return '-';
      const dt = new Date(d);
      if (Number.isNaN(dt.getTime())) return d;
      return dt.toLocaleDateString('pt-BR', { timeZone: 'UTC' });
    },
  },
};
</script>

<style scoped>
.page-breadcrumb {
  font-size: 0.85rem;
  color: #6e6b7b;
}
</style>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';

.budget-vselect-sm .vs__dropdown-toggle {
  min-height: 31px;
}
</style>
